/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 29/3/2023
 */

import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  OutlinedInput,
  FormHelperText
} from "@mui/material";
import { useState , useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { toast } from "react-toastify";
import { useGetUserBrQuery } from "../../Store/Services/UserService";
import * as Yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import styles from "./AddUser.module.css";
import { useCreateUserMutation } from "../../Store/Services/UserService";

const schema = object({
  firstName: Yup.string().required(""),
  lastName: Yup.string().required(""),
  userName: Yup.string().required(""),
  email: Yup.string().required("").matches("^[a-zA-Z0-9+._-]+@[a-zA-Z0-9.-]+.[a-z]+$", "Email format is invalid!"),
  telephone: Yup.string().required(""),
  mobile: Yup.string().required("([0-9][^0-9]){8,10}$"),
  password: Yup.string()
    .min(
      8,
      "password should be with 1 uppercase 1 lowercase 1 number 1 special character and it should be more than 8 characters  "
    )
    .required("Password is required!")
    .max(
      12,
      "password should be with 1 uppercase 1 lowercase 1 number 1 special character and it should be more than 8 characters  "
    )
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    ),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "Passwords must match"
  ),
}).required();

const AddUser = ({ handleClose }) => {
  const { data, isSuccess: brSuccess } = useGetUserBrQuery();

  const [userBr, setUserBr] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [userCreateResponse] = useCreateUserMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  let brList;
  if (brSuccess) {
    brList = data?.map((br) => ({ brId: br.brId, brName: br.brName }));
  }

  

    const brMap = useMemo(() => {
      const map = new Map();
      brList?.forEach(br => map.set(br.brId, br));
      return map;
    }, [brList]);

    const selectedBrObjects = userBr.map(brId => brMap.get(brId)).filter(Boolean);

   
  const onSubmit = async (data) => {
  
    const dataSet = {
      ...data,
      newRoles: userBr,
    };
    if(userBr.length !== 0){
      try {
        const userUpdate = await userCreateResponse(dataSet).unwrap();
        toast.success("User successfully Created!" , {position:'bottom-right'});
      } catch (err) {
        console.error("Failed to edit user details: ", err);
        toast.error(err.data?.message , {position:'bottom-right'});
      }
      handleClose();
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((currentShowState) => !currentShowState);
  };

  const onKeyDown = (event) => {
    if (!/[0-9]/.test(event.key) && event.key !== "Backspace") {
      event.preventDefault();
    }
  };

  return (
    <div>
      <div className={styles.headingContainer}>
        <Typography style={{display:'flex',justifyContent:'center'}} fontWeight={600} variant={"h6"}>
          Add User
        </Typography>
        <IconButton className={styles.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className={styles.imageDev}>
          <Avatar
            style={{
              width: "110px",
              height: "110px",
              border: "solid 1px #9e1618",
            }}
            src={`${process.env.REACT_APP_IMAGE_URL}INVENTORY%2Favatar.png`}
          />
        </div>

        <div className={styles.formItem}>
          <label className={styles.formLabel}> First Name :</label>
          {errors.firstName && errors.firstName.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <TextField
            {...register("firstName")}
            className={styles.formInput}
            placeholder="Enter FirstName"
            multiline
            rows={1}
            size="small"
            fullWidth
          />
        </div>

        <div className={styles.formItem}>
          <label className={styles.formLabel}> Last Name :</label>
          {errors.lastName && errors.lastName.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <TextField
            {...register("lastName")}
            className={styles.formInput}
            placeholder="Enter LastName"
            multiline
            rows={1}
            size="small"
            fullWidth
          />
        </div>

        <div className={styles.formItem}>
          <label className={styles.formLabel}> User Name :</label>
          {errors.userName && errors.userName.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <TextField
            {...register("userName")}
            className={styles.formInput}
            placeholder="Enter Username"
            multiline
            rows={1}
            size="small"
            fullWidth
          />
        </div>
        <div className={styles.formItem}>
          <label className={styles.formLabel}> Email :</label>
          {errors.email && errors.email.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <TextField
            {...register("email")}
            className={styles.formInput}
            placeholder="Enter Email"
            multiline
            rows={1}
            size="small"
            fullWidth
          />
        </div>
        <div className={styles.formItem}>
          <label className={styles.formLabel}> Telephone :</label>
          {errors.telephone && errors.telephone.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <TextField
            {...register("telephone")}
            className={styles.formInput}
            placeholder="Enter Telephone Number"
            size="small"
            multiline
            onKeyDown={onKeyDown}
            rows={1}
            fullWidth
          />
        </div>

        <div className={styles.formItem}>
          <label className={styles.formLabel}> Mobile :</label>
          {errors.mobile && errors.mobile.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <TextField
            {...register("mobile")}
            className={styles.formInput}
            placeholder="Enter Mobile Number"
            size="small"
            onKeyDown={onKeyDown}
            multiline
            rows={1}
            fullWidth
          />
        </div>

        <div className={styles.formItem}>
          <label className={styles.formLabel}>Password :</label>
          <TextField
            {...register("password", { minLength: 8 })}
            size="small"
            fullWidth
            className={styles.formInput}
            placeholder="Enter Password"
            type={showPassword ? "text" : "password"}
            error={!!errors.userPsw}
            helperText={errors.userPsw?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        {/* {errors.password && <FormHelperText error> {errors.password?.message} </FormHelperText>  } */}
        <div className={styles.formItem}>
          <label className={styles.formLabel}>
            Confirm Password :
          </label>
          <TextField
            {...register("confirmPassword", { minLength: 8 })}
            size="small"
            fullWidth
            className={styles.formInput}
            placeholder="Confrm Password:"
            type={showPassword ? "text" : "password"}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={styles.formItem}>
          <label className={styles.formLabel}>User Br:</label>
          {userBr.length===0 && (<span className={styles.validation}> *</span>)}
          {brSuccess && (
            <Autocomplete
              className={styles.formInput}
              onChange={(_, newBr) => {
                setUserBr(newBr.map((br) => br.brId));
              }}
              size="small"
              value={selectedBrObjects}
              multiple
              filterSelectedOptions
              isOptionEqualToValue={(option, value) =>
                option.brId === value.brId
              }
              options={data}
              getOptionLabel={(option) => option.brName}
              renderInput={(params) => (
                <TextField placeholder="User Br" {...params} />
              )}
            />
          )}
        </div>
        <div className={styles.buttonContainer}>
          <Button
            type="submit"
            className={styles.submitButton}
            variant="contained"
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddUser;
